import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Inline SVG icons                                                    */
/* ------------------------------------------------------------------ */

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function IconFilm() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
    </svg>
  )
}

function IconHeart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
    </svg>
  )
}

function IconEye() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function IconScale() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function About({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data - AboutPage */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About QuiteRight",
          description: meta.description,
          url: meta.canonical,
        })}</script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">About</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            We exist to help agents win<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            More instructions. Faster sales. Happier vendors. That&rsquo;s the only
            metric that matters - and everything we build is designed to move those numbers.
          </p>
        </div>
      </section>

      {/* The story — framed as understanding the agent's world */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-3xl">
          {/* Clifton Suspension Bridge */}
          <div className="mb-12 overflow-hidden rounded-xl border border-stone shadow-sm">
            <OptimizedImage
              eager
              src={cdnUrl("/clifton-bridge-800w.webp")}
              srcSet={imageSrcSet("/clifton-bridge-800w.webp")}
              sizes="(max-width: 768px) 100vw, 768px"
              alt="Watercolour illustration of the Clifton Suspension Bridge spanning the Avon Gorge in Bristol"
              className="w-full"
              width={1024}
              height={440}
            />
            <div className="bg-cream px-4 py-2.5 text-center">
              <p className="text-xs text-warm-grey">Built in Clifton, Bristol - where every street deserves a scroll-stopper.</p>
            </div>
          </div>

          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Why we built this</p>
          <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
            We watched agents lose instructions they should have won<span className="text-red">.</span>
          </h2>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-slate">
            <p>
              We spoke to dozens of estate agents. The same story came up every time:
              &ldquo;The vendor went with the other agent because they offered video.&rdquo;
              Not because they were better. Not because they had more experience.
              Because they had a 30-second video to show in the market appraisal.
            </p>
            <p>
              The agents we spoke to knew video worked. They could see it on every social
              platform. The problem was always the same: getting professional video produced
              is slow, expensive, and complicated. By the time the videographer edited the
              footage, the property was already under offer.
            </p>
            <p>
              So we built QuiteRight. A platform that turns any property listing into a
              polished, branded video ad in under a minute. No filming. No editing.
              No waiting. Just paste the URL, pick a template, and publish to every platform
              with one click.
            </p>
            <p>
              Now the agents we work with walk into market appraisals with video
              ready to show. They post to five social platforms before lunch.
              They&rsquo;re the agents vendors choose - because they look like they
              take marketing seriously. Because they do.
            </p>
          </div>
        </div>
      </section>

      {/* The agent's reality — problem/solution framing */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
            The agents who win instructions vs. the ones who don&rsquo;t<span className="text-red">.</span>
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* Without */}
            <div className="rounded-lg border border-stone bg-cream p-8">
              <h3 className="font-display text-xl font-bold text-charcoal">
                Without video marketing<span className="text-red">.</span>
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  "Vendors compare you to agents who offer video - and choose them",
                  "Listings sit on portals looking identical to every competitor",
                  "Social media gets a few likes on a photo carousel",
                  "Market appraisals rely on floor plans and promises",
                  "Buyers scroll past your listing in under a second",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="#C41E2A" className="mt-0.5 size-4 shrink-0">
                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* With */}
            <div className="rounded-lg border border-red/20 bg-red/5 p-8">
              <h3 className="font-display text-xl font-bold text-charcoal">
                With QuiteRight<span className="text-red">.</span>
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  "Walk into appraisals with a video ready to show the vendor",
                  "Listings stop the scroll with branded, professional video",
                  "Five social platforms reached with one click - every listing",
                  "Vendors see a premium marketing package before they sign",
                  "Properties sell faster because more buyers see them",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="#C41E2A" className="mt-0.5 size-4 shrink-0">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What we believe — framed as promises to agents */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Our promises to you</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-cream md:text-4xl">
              What you can always expect from us<span className="text-red">.</span>
            </h2>
          </div>
          <ScrollReveal stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: IconEye,
                title: "You get the credit",
                description: "Your name, your logo, your brand on every video. Vendors and buyers see you - not us. The technology is invisible. The results aren't.",
              },
              {
                icon: IconHome,
                title: "Built for property, not everything",
                description: "We're not a generic video tool. Every template, script, and feature is designed specifically for agents who sell and let properties in the UK.",
              },
              {
                icon: IconBolt,
                title: "Speed that matches your business",
                description: "Properties move fast. A videographer takes a week. QuiteRight takes 60 seconds. Your marketing is live before the competition even books a shoot.",
              },
              {
                icon: IconHeart,
                title: "We build what agents actually need",
                description: "If it doesn't help you win an instruction, sell a property, or fill a vacancy - it doesn't ship. Every feature is tested against one question: does this make agents' lives easier?",
              },
              {
                icon: IconScale,
                title: "Transparent, fair pricing",
                description: "No hidden fees. No surprise charges. No annual contracts. Pricing that makes sense for solo agents and multi-branch agencies alike.",
              },
              {
                icon: IconFilm,
                title: "Quality you'd be proud to show vendors",
                description: "Professional voiceovers, curated music, polished templates. Every video should be good enough for your best listing, your biggest vendor, your toughest market appraisal.",
              },
            ].map((value) => {
              const Icon = value.icon
              return (
                <div key={value.title} className="rounded-lg border border-slate bg-charcoal p-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                    <Icon />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-cream">{value.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-warm-grey">{value.description}</p>
                </div>
              )
            })}
          </ScrollReveal>
        </div>
      </section>

      {/* Who we serve — specific agent types */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Who we serve</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              Built for the people who sell and let properties<span className="text-red">.</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Estate agents",
                description: "Solo agents, high-street agencies, and multi-branch operations. Win more instructions with video that shows vendors you're serious about marketing their property.",
              },
              {
                title: "Letting agents",
                description: "Letting agencies and property managers. Fill vacancies faster with video that attracts quality tenants and shows landlords the marketing effort behind every listing.",
              },
              {
                title: "Holiday let owners",
                description: "Short-term rental hosts on Airbnb and Booking.com. Stand out from the crowd and drive direct bookings with video tours that make guests picture themselves there.",
              },
            ].map((audience) => (
              <div key={audience.title} className="rounded-lg border border-stone bg-cream p-6 text-center">
                <h3 className="font-display text-lg font-semibold text-charcoal">{audience.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry & community */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center font-display text-2xl font-bold text-charcoal md:text-3xl">
            Part of the UK property community<span className="text-red">.</span>
          </h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-sm leading-relaxed text-slate">
            <p>
              The UK property industry is built on relationships, local knowledge, and trust. We believe
              technology should amplify those strengths, not replace them. QuiteRight exists to give
              every estate agent, from a single-office independent to a national brand, the
              same calibre of video marketing that was previously reserved for agencies with five-figure
              budgets.
            </p>
            <p>
              We work alongside the organisations that shape the UK property landscape, including{" "}
              <a href="https://www.propertymark.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Propertymark, the leading professional body for property agents">
                Propertymark
              </a>
              {" "}(the leading professional body for property agents),{" "}
              <a href="https://www.tpos.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="The Property Ombudsman">
                The Property Ombudsman
              </a>
              , and the major listing portals{" "}
              <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Rightmove, the UK's largest property portal">
                Rightmove
              </a>
              ,{" "}
              <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Zoopla, property search and valuation platform">
                Zoopla
              </a>
              , and{" "}
              <a href="https://www.onthemarket.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="OnTheMarket, agent-owned property portal">
                OnTheMarket
              </a>
              . Our platform integrates directly with these portals so agents can turn any live listing
              into a professional video without leaving their browser.
            </p>
            <p>
              Based in Clifton, Bristol, we&rsquo;re proud to be part of the{" "}
              <a href="https://www.bristolcreativeindustries.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Bristol Creative Industries">
                Bristol creative technology
              </a>
              {" "}scene and the growing community of UK property technology companies making the industry
              more efficient, more accessible, and more transparent for everyone.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
            Join the agents who stopped hoping and started winning<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
            Try it free. Your first video takes 60 seconds. The instruction it wins
            you could be the best return on investment you&rsquo;ve ever made.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See it with your listings
            </Link>
            <Link
              href="/contact"
              className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
